/**
 * Exceções do robô — pedido da Mariana (24/09/2026): "eu ativo e sinalizo exceção e prazo de
 * término, pronto". Um anúncio marcado aqui (com motivo e prazo) fica fora do alcance do robô:
 * o motor não manda sair nem alerta até a data. A tela avisa quando faltam 3 dias e quando venceu;
 * vencida, o robô volta a tratar o anúncio como qualquer outro.
 * Gravado em excecoes_motor.json pelo serviço (X-Token só no servidor); histórico na aba Histórico.
 */
import { useCallback, useEffect, useState } from 'react'
import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import { fetchExcecoes, removerExcecao, salvarExcecao, type Excecao, type Excecoes } from './promocoesService'
import { EVENTO_EXCECAO as EVENTO, consumirExcecaoPendente } from './logic'
import { usePromo } from './ctx'
import { LARANJA, P, Panel, mono } from './ui'

const hojeMais = (dias: number) => {
  const d = new Date()
  d.setDate(d.getDate() + dias)
  return d.toISOString().slice(0, 10)
}

const ddmmaaaa = (iso: string) => {
  const [a, m, d] = iso.split('-')
  return `${d}/${m}/${a}`
}

export function ExcecoesRobo() {
  const ctx = usePromo()
  const [dados, setDados] = useState<Excecoes | null>(null)
  const [mlb, setMlb] = useState('')
  const [titulo, setTitulo] = useState('')
  const [motivo, setMotivo] = useState('')
  const [ate, setAte] = useState(hojeMais(30))
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // 02/10/2026, ela: "deveria poder incluir exceções que eu quiser, apenas digitando o MLB" — o formulário
  // fica sempre aberto, só o MLB é obrigatório; motivo vazio vira "exceção marcada na tela", prazo padrão 30 dias.
  const [aberto, setAberto] = useState(true)

  const carregar = useCallback(() => {
    void fetchExcecoes().then((d) => setDados(d))
  }, [])
  useEffect(() => {
    carregar()
  }, [carregar])

  useEffect(() => {
    const h = (e: Event) => {
      const det = (e as CustomEvent<{ mlb: string; titulo?: string }>).detail
      setMlb(det.mlb)
      setTitulo(det.titulo || '')
      setAberto(true)
      setMsg(null)
      document.getElementById('promo-excecoes')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    window.addEventListener(EVENTO, h)
    // pedido feito antes de este painel existir (botão "exceção" da Revisão humana, aba Alertas)
    const pendente = consumirExcecaoPendente()
    if (pendente) window.setTimeout(() => h(new CustomEvent(EVENTO, { detail: pendente })), 0)
    return () => window.removeEventListener(EVENTO, h)
  }, [])

  const salvar = async () => {
    setBusy(true)
    setMsg(null)
    try {
      await salvarExcecao({ mlb: mlb.trim().toUpperCase(), motivo: motivo.trim() || 'exceção marcada na tela', ate, titulo, conta: ctx.contaId })
      setMsg(`exceção gravada até ${ddmmaaaa(ate)} — o motor já rodou de novo`)
      setMlb('')
      setTitulo('')
      setMotivo('')
      carregar()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const remover = async (x: Excecao) => {
    setBusy(true)
    try {
      await removerExcecao(x.mlb)
      carregar()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const renovar = (x: Excecao) => {
    setMlb(x.mlb)
    setTitulo(x.titulo || '')
    setMotivo(x.motivo)
    setAte(hojeMais(30))
    setAberto(true)
  }

  const lista = dados?.excecoes ?? []
  const avisos = lista.filter((x) => x.estado !== 'vigente')
  const corEstado = (x: Excecao) => (x.estado === 'vencida' ? 'error.main' : x.estado === 'vencendo' ? LARANJA : 'text.secondary')
  const rotuloEstado = (x: Excecao) =>
    x.estado === 'vencida' ? `VENCIDA há ${Math.abs(x.dias_restantes ?? 0)} dia(s) — o robô já pode agir` : x.estado === 'vencendo' ? `vence em ${x.dias_restantes} dia(s)` : `até ${ddmmaaaa(x.ate)} (${x.dias_restantes} dias)`

  return (
    <Box id="promo-excecoes">
      <Panel
        title="🔒 Exceções do robô — anúncios que você ativou na mão e o robô não pode tirar"
        tone={avisos.length ? 'alert' : undefined}
        count={dados ? `${lista.length} ${lista.length === 1 ? 'exceção' : 'exceções'}${dados.vencendo ? ` · ${dados.vencendo} vencendo` : ''}${dados.vencidas ? ` · ${dados.vencidas} vencida(s)` : ''}` : undefined}
      >
        <P>
          Digite o <b>MLB</b> e salve: até a data o robô não sai, não troca e não alerta nesse anúncio (cupom continua entrando). Motivo é
          opcional (ajuda você depois); prazo padrão 30 dias — faltando {dados?.dias_aviso ?? 3} dias este painel avisa, e vencida o robô volta a
          agir. Salvar já roda o motor de novo. Para uma <b>lista de MLBs com piso próprio</b> (ex.: Natal), use o painel <b>Grupos com regra própria</b> logo abaixo.
          {!ctx.escreve ? <><br /><b>Serviço fora do ar ou em preview: só leitura.</b></> : null}
        </P>

        {avisos.length ? (
          <Stack spacing={0.25} sx={{ mb: 1 }}>
            {avisos.map((x) => (
              <Typography key={x.mlb} sx={{ fontSize: 12.5, color: corEstado(x) }}>
                ⚠ <Box component="span" sx={mono}>{x.mlb}</Box> {x.titulo ? `· ${x.titulo.slice(0, 50)} ` : ''}— {rotuloEstado(x)} · {x.motivo}
              </Typography>
            ))}
          </Stack>
        ) : null}

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Button size="small" variant={aberto ? 'text' : 'outlined'} color="inherit" onClick={() => setAberto((v) => !v)} disabled={!ctx.escreve}>
            {aberto ? 'fechar' : '+ marcar exceção'}
          </Button>
          {msg ? <Typography sx={{ fontSize: 12 }}>{msg}</Typography> : null}
        </Stack>

        {aberto ? (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }} sx={{ mb: 1.5 }}>
            <TextField size="small" label="MLB" value={mlb} onChange={(e) => setMlb(e.target.value.toUpperCase().replace(/\s+/g, ''))} onKeyDown={(e) => { if (e.key === 'Enter' && /^MLB\d{6,}$/.test(mlb.trim()) && ctx.escreve && !busy) void salvar() }} sx={{ minWidth: 190 }} helperText={titulo ? titulo.slice(0, 40) : (ctx.conta.ads.find((a) => a.mlb === mlb.trim())?.titulo?.slice(0, 40) ?? (mlb && !/^MLB\d{6,}$/.test(mlb) ? 'ex.: MLB1234567890' : ' '))} autoFocus={Boolean(titulo)} />
            <TextField size="small" label="Motivo (opcional)" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="queima de estoque parado" sx={{ flex: 1, minWidth: 220 }} helperText=" " />
            <TextField size="small" type="date" label="Manter até" value={ate} onChange={(e) => setAte(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: hojeMais(0) }} helperText="o robô volta a agir depois desta data" />
            <Button variant="contained" size="small" onClick={() => void salvar()} disabled={busy || !ctx.escreve || !/^MLB\d{6,}$/.test(mlb.trim()) || !ate} sx={{ mt: { md: 0.25 } }}>
              Salvar exceção
            </Button>
          </Stack>
        ) : null}

        {lista.length ? (
          lista.map((x) => (
            <Box key={x.mlb} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1.6fr auto auto' }, gap: 1.25, alignItems: 'center', py: 0.75, borderTop: 1, borderColor: 'divider', fontSize: 12.5 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontSize: 13, cursor: 'pointer' }} onClick={() => ctx.abrirAnuncio(x.mlb)} title={x.titulo || x.mlb}>
                  {x.titulo || x.mlb}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                  <Box component="span" sx={mono}>{x.mlb}</Box>
                  {x.por ? ` · por ${x.por}` : ''}
                  {x.margem_minima != null ? ` · piso próprio ${x.margem_minima}%` : ' · sem piso: nunca sai'}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12.5 }}>{x.motivo}</Typography>
              <Typography sx={{ fontSize: 12.5, color: corEstado(x), fontWeight: x.estado === 'vigente' ? 400 : 700 }}>{rotuloEstado(x)}</Typography>
              <Stack direction="row" spacing={0.5}>
                <Button size="small" variant="text" color="inherit" onClick={() => renovar(x)} disabled={!ctx.escreve || busy}>
                  renovar
                </Button>
                <Button size="small" variant="text" color="error" onClick={() => void remover(x)} disabled={!ctx.escreve || busy}>
                  remover
                </Button>
              </Stack>
            </Box>
          ))
        ) : (
          <Typography sx={{ color: 'text.disabled', fontSize: 13 }}>Nenhuma exceção marcada.</Typography>
        )}
      </Panel>
    </Box>
  )
}
